import { useState, useEffect, useCallback } from 'react';
import { authService, type UserDto } from '../services/auth';

export const useAuth = () => {
  const [user, setUser] = useState<UserDto | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Auth is based on shared public cookie (arrangelly_user), not on /auth/me API call.
        const currentUser = await authService.getCurrentUser();
        setUser(currentUser);
      } catch {
        // Not authenticated (no cookie or cookie invalid)
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await authService.login({ email, password });
      authService.setAuth(response);
      setUser(response.user);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  }, []);

  const register = useCallback(async (email: string, password: string, firstName?: string, lastName?: string) => {
    try {
      const response = await authService.register({ email, password, firstName, lastName });
      authService.setAuth(response);
      setUser(response.user);
      return { success: true };
    } catch (error: any) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Registration failed' 
      };
    }
  }, []);

  const registerInvitedTeacher = useCallback(
    async (email: string, password: string, invitationToken: string, firstName?: string, lastName?: string) => {
      try {
        const response = await authService.registerInvitedTeacher({
          email,
          password,
          invitationToken,
          firstName,
          lastName,
        });
        authService.setAuth(response);
        setUser(response.user);
        return { success: true };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || 'Registration failed',
        };
      }
    },
    []
  );

  const registerInvitedStudent = useCallback(
    async (email: string, password: string, invitationToken: string, firstName?: string, lastName?: string) => {
      try {
        const response = await authService.registerStudent({
          email,
          password,
          invitationToken,
          firstName,
          lastName,
        });
        authService.setAuth(response);
        setUser(response.user);
        return { success: true };
      } catch (error: any) {
        return {
          success: false,
          error: error.response?.data?.message || 'Registration failed',
        };
      }
    },
    []
  );

  const logout = useCallback(async () => {
    setUser(null);
    // Dispatch custom event to notify other useAuth instances
    window.dispatchEvent(new Event('auth:logout'));
    // authService.logout() will handle the API call and navigation
    await authService.logout();
  }, []);

  return {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    registerInvitedTeacher,
    registerInvitedStudent,
    logout,
  };
};

